import { Chip, FormControl, FormControlLabel, Grid, MenuItem, Select, Switch, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../orders/Orders.scss';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { Cancel, Check, Link as MuiLinkIcon } from '@mui/icons-material';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { Link, useSearchParams } from 'react-router-dom';
import TimeUtils from '../utils/TimeUtils';
import {
  AccountData,
  ArchiveReason,
  EnrichedPaymentLink,
  OrderPageData,
  PaymentLinkPaymentStatus,
  PaymentLinkService,
  PaymentLinkType,
} from '../api';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useLanguage } from '../common/GeneralUtils';
import { getOrderPages } from '../utils/AccountUtils';
import { CalendarView, DATE_FORMAT } from '../calendar/calendarWrapper/CalendarWrapper';
import { CellProps, useTable } from '../table/useTable';
import { useAuth } from '../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { getLinkToPaymentLink, getPaymentLinkStatus } from '../calendar/dialogs/EditOrderDialog/OrderDetails';
import CopyToClipboardButton from '../calendar/dialogs/CopyToClipboardButton';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { PriceCell } from '../table/PriceCell';

const ALL_STATUSES = [PaymentLinkPaymentStatus.PAID, PaymentLinkPaymentStatus.PENDING_PAYMENT];
export const paymentLinkFetcher =
  (statuses?: PaymentLinkPaymentStatus[], withPastOrders?: boolean, withArchivedPaymentLinks?: boolean) =>
  async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) =>
    PaymentLinkService.getPaymentLinks(
      page,
      perPage,
      search,
      statuses,
      withPastOrders || false,
      withArchivedPaymentLinks || false,
    );

export const PaymentLinkTable = React.memo(() => {
  const { editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);
  const language = useLanguage();
  const [searchParams] = useSearchParams();
  const [withPastOrders, setWithPastOrders] = useState(false);
  const [withArchivedPaymentLinks, setWithArchivedPaymentLinks] = useState(false);

  // Initialize selected statuses from URL params or default to all
  const getInitialStatuses = () => {
    const statusParam = searchParams.get('status');
    if (statusParam === 'pending_payment') {
      return [PaymentLinkPaymentStatus.PENDING_PAYMENT];
    }
    return ALL_STATUSES;
  };

  const [selectedStatuses, setSelectedStatuses] = useState<Array<PaymentLinkPaymentStatus>>(getInitialStatuses);

  function formatCreatedAt(created_at: string) {
    const createdAt = TimeUtils.getLocalTime(created_at, account.timezone);
    const isToday = createdAt.isSame(moment(), 'day');
    return (
      <Typography
        variant={isToday ? 'body1' : 'body2'}
        color={isToday ? 'primary.main' : undefined}
        fontWeight={isToday ? 700 : undefined}>
        {createdAt.format(isToday ? timeFormat : shortDateFormat)}
      </Typography>
    );
  }

  const tableDetails: CellProps<EnrichedPaymentLink>[] = [
    {
      id: 'orderName',
      alignment: 'center',
      label: t('orders.name'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { order } = enrichedPaymentLink;
        return `${order.firstname} ${order.lastname}`;
      },
    },
    {
      id: 'orderCreatedAt',
      alignment: 'center',
      label: t('order_creation_date'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { order } = enrichedPaymentLink;
        return formatCreatedAt(order.created_at);
      },
    },
    {
      id: 'orderDate',
      alignment: 'center',
      label: t('order_date'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { order } = enrichedPaymentLink;
        if (!order.date) return '-';
        const momentDate = moment(order.date);
        const calendarView = localStorage.getItem('webook-calendarview');
        const weekStart = momentDate
          .clone()
          .startOf(calendarView === CalendarView.THREE_DAY ? 'day' : (calendarView as any));
        return (
          <Link to={`/dashboard/calendar/${weekStart.format(DATE_FORMAT)}?entityid=${order.parent_id || order.id}`}>
            {momentDate.format(shortDateFormat)}
          </Link>
        );
      },
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: t('orders.workshop'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { order } = enrichedPaymentLink;
        const orderPage = getOrderPages(account).find((x: OrderPageData) => x.id === order.order_page_id);
        return (
          <Grid container gap={1} flexWrap="nowrap" alignItems={'center'}>
            <WorkshopCircle account={account} orderPageId={order.order_page_id} language={language} withTooltip />
            <Typography
              variant={'body2'}
              sx={{
                maxWidth: 100,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {orderPage?.label[language]}
            </Typography>
          </Grid>
        );
      },
    },
    {
      id: 'orderNumber',
      alignment: 'center',
      label: t('order_number'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { order } = enrichedPaymentLink;
        return order.order_number ? `#${order.order_number}` : '-';
      },
    },
    {
      id: 'title',
      alignment: 'justify',
      label: t('paymentlink.title'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { payment_link } = enrichedPaymentLink;
        return (
          <Grid sx={{ maxWidth: 300 }}>
            <Typography sx={{ wordWrap: 'break-word', whiteSpace: 'pre-line' }}>
              {payment_link.payment_link_type === PaymentLinkType.MAIN_PAYMENT
                ? t('paymentlink.paymentForOrder')
                : payment_link.title}
            </Typography>
          </Grid>
        );
      },
    },
    {
      // im not useing the regular price rendering as it needs to get another type of object
      id: 'custom-price',
      alignment: 'center',
      label: t('paymentlink.price'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => (
        <PriceCell orderOrVoucher={enrichedPaymentLink.order} isVoucher={false} />
      ),
    },
    {
      id: 'paymentLinkCreatedAt',
      alignment: 'center',
      label: t('payment_link_creation_date'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => (
        <Typography>{moment(enrichedPaymentLink.payment_link.created_at).format(shortDateFormat)}</Typography>
      ),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('paymentlink.statusCol'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => {
        const { payment_link, order } = enrichedPaymentLink;
        const isExpiredHold = payment_link.archived && order.archive_reason === ArchiveReason.EXPIRED;
        const holdsUntil =
          !payment_link.archived &&
          payment_link.payment_status === PaymentLinkPaymentStatus.PENDING_PAYMENT &&
          order.pending_expires_at
            ? TimeUtils.getLocalTime(order.pending_expires_at, account.timezone)
            : undefined;
        return (
          <Grid container direction="column" alignItems="center" gap={0.5}>
            {getPaymentLinkStatus(payment_link.payment_status, t, payment_link.archived)}
            {isExpiredHold && (
              <Chip size="small" color="error" variant="outlined" label={t('paymentlink.holdExpired')} />
            )}
            {holdsUntil && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t('paymentlink.holdsUntil', { time: holdsUntil.format(`${shortDateFormat} ${timeFormat}`) })}
              />
            )}
          </Grid>
        );
      },
    },
    {
      id: 'orderlink',
      alignment: 'center',
      label: t('vouchers.orderlink'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => (
        <Grid
          sx={{ cursor: 'pointer' }}
          item
          onClick={() => {
            openEditOrderDrawer({ order: enrichedPaymentLink.order });
          }}>
          <Tooltip title={t('info')} arrow>
            <ConfirmationNumber sx={{ color: 'text.secondary' }} />
          </Tooltip>
        </Grid>
      ),
    },
    {
      id: 'link',
      alignment: 'center',
      label: t('paymentlink.link'),
      render: (enrichedPaymentLink: EnrichedPaymentLink) => (
        <CopyToClipboardButton
          IconComponent={MuiLinkIcon}
          text={getLinkToPaymentLink(enrichedPaymentLink.payment_link.id)}
        />
      ),
    },
  ];

  const { TableComponent, mutate: reFetch } = useTable<EnrichedPaymentLink>({
    opId: 'getPaymentLinks',
    fetcher: paymentLinkFetcher(selectedStatuses, withPastOrders, withArchivedPaymentLinks),
    tableDetails,
    fillHeight: true,
    hideSearch: true,
    actionsComponent: (
      <Grid container gap={1}>
        <FormControl>
          <Grid container alignItems="center" gap={1}>
            <Typography>{t('paymentlink.statusLabel')}</Typography>
            <Select
              sx={{ minWidth: { xs: 200, sm: 300 } }}
              size="small"
              multiple
              value={selectedStatuses}
              renderValue={(selected) => (
                <Grid container gap={1}>
                  {selected.map((status) => (
                    <Chip
                      key={status}
                      label={t(`paymentlink.status.${status}`)}
                      onDelete={() => {
                        const remainingStatuses = selected.filter((s) => s !== status);
                        setSelectedStatuses(remainingStatuses.length === 0 ? ALL_STATUSES : remainingStatuses);
                      }}
                      deleteIcon={<Cancel onMouseDown={(event) => event.stopPropagation()} />}
                    />
                  ))}
                </Grid>
              )}
              onChange={(e) =>
                (e.target.value as Array<PaymentLinkPaymentStatus>).length === 0
                  ? setSelectedStatuses(ALL_STATUSES)
                  : setSelectedStatuses(e.target.value as Array<PaymentLinkPaymentStatus>)
              }>
              <MenuItem value={PaymentLinkPaymentStatus.PENDING_PAYMENT}>
                <Grid container alignItems="center">
                  <Grid item xs>
                    {t('paymentlink.status.pending_payment')}
                  </Grid>
                  {selectedStatuses.includes(PaymentLinkPaymentStatus.PENDING_PAYMENT) && (
                    <Grid item>
                      <Check />
                    </Grid>
                  )}
                </Grid>
              </MenuItem>
              <MenuItem value={PaymentLinkPaymentStatus.PAID}>
                <Grid container alignItems="center">
                  <Grid item xs>
                    {t('paymentlink.status.paid')}
                  </Grid>
                  {selectedStatuses.includes(PaymentLinkPaymentStatus.PAID) && (
                    <Grid item>
                      <Check />
                    </Grid>
                  )}
                </Grid>
              </MenuItem>
            </Select>
          </Grid>
        </FormControl>
        <Grid container>
          <FormControl>
            <FormControlLabel
              control={<Switch checked={withPastOrders} onChange={(e) => setWithPastOrders(e.target.checked)} />}
              label={t('paymentlink.withPastOrders') || 'Show Past Orders'}
            />
          </FormControl>
          <FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={withArchivedPaymentLinks}
                  onChange={(e) => setWithArchivedPaymentLinks(e.target.checked)}
                />
              }
              label={t('paymentlink.withArchivedPaymentLinks') || 'Show Archived Payment Links'}
            />
          </FormControl>
        </Grid>
      </Grid>
    ),
  });
  useEffect(() => {
    void reFetch();
  }, [selectedStatuses, withPastOrders, withArchivedPaymentLinks, reFetch]);

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void closeEditOrderDrawer();
        }}
      />
    </Grid>
  );
});
